// Sesión del usuario.
//
// El estado real de la sesión vive en la cookie httpOnly del servidor; acá
// solo se cachea quién es para poder decidir qué mostrar. La autorización
// SIEMPRE la decide la API: esconder un botón es comodidad, no seguridad.
import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { api, ErrorAPI } from './api';
import type { Rol, Usuario } from './tipos';

interface Sesion {
  usuario: Usuario | null;
  cargando: boolean;
  ingresar: (email: string, clave: string) => Promise<void>;
  salir: () => Promise<void>;
  puede: (...roles: Rol[]) => boolean;
  soloLectura: boolean;
}

const Ctx = createContext<Sesion | null>(null);

export function ProveedorSesion({ children }: { children: ReactNode }) {
  const [usuario, setUsuario] = useState<Usuario | null>(null);
  const [cargando, setCargando] = useState(true);

  useEffect(() => {
    api.get<Usuario>('/api/yo')
      .then(setUsuario)
      .catch((e) => {
        // 401 al arrancar es lo normal: todavía no inició sesión.
        if (!(e instanceof ErrorAPI && e.status === 401)) console.error(e);
        setUsuario(null);
      })
      .finally(() => setCargando(false));
  }, []);

  const valor: Sesion = {
    usuario,
    cargando,
    ingresar: async (email, clave) => {
      setUsuario(await api.post<Usuario>('/api/login', { email, clave }));
    },
    salir: async () => {
      await api.post('/api/logout');
      setUsuario(null);
    },
    puede: (...roles) => !!usuario && roles.includes(usuario.rol),
    soloLectura: usuario?.rol === 'auditor',
  };

  return <Ctx.Provider value={valor}>{children}</Ctx.Provider>;
}

export function useSesion(): Sesion {
  const s = useContext(Ctx);
  if (!s) throw new Error('useSesion tiene que usarse dentro de ProveedorSesion');
  return s;
}
